import * as layout from '../markLayout.module.css';
import { MarkButton, MarkInput, MarkTextArea, MarkLabel } from '../MarkControls';
import React, { useState, useCallback, useId, useMemo, useImperativeHandle } from 'react';
import * as canvasStyles from './canvas.module.css';
import P from '@hero/ui-kit/typography/P';
import type { AxiosError } from 'axios';
import type { BlockDoc, Channel } from '../../types';
import useTemplatePreview from '../../api/useTemplatePreview';
import EmailPreviewFrame from '../EmailPreviewFrame';
import { describeErrorCode } from './constants';
import { useInsertTarget } from './useInsertTarget';
type Props = {
    blockDoc: BlockDoc;
    channel: Channel;
    parentSubject?: string;
    compact?: boolean;
    syntheticOnly?: boolean;
};
const extractErrorCodes = (error: AxiosError): string[] => {
    const data = error.response?.data as
        | {
              errors?: string[];
          }
        | undefined;
    return data?.errors ?? [];
};
export type PreviewHandle = { refresh: () => void };
const PreviewPanel = React.forwardRef<PreviewHandle, Props>(
    ({ blockDoc, channel, parentSubject, compact = false, syntheticOnly = false }, ref) => {
        const preview = useTemplatePreview();
        const [subject, setSubject] = useState('');
        const [profileId, setProfileId] = useState('');
        const [variablesJson, setVariablesJson] = useState('');
        const [jsonError, setJsonError] = useState('');
        const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
        const previewId = useId();
        const subjectId = `${previewId}-subject`;
        const profileIdFieldId = `${previewId}-profile-id`;
        const variablesId = `${previewId}-variables`;
        const { clearInsertTarget } = useInsertTarget();
        const effectiveSubject = subject || parentSubject;
        const currentDocHash = useMemo(() => JSON.stringify(blockDoc), [blockDoc]);
        const parseVariablesJson = (): Record<string, string> | undefined | false => {
            if (!variablesJson.trim()) return undefined;
            try {
                const parsed = JSON.parse(variablesJson.trim());
                if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                    setJsonError('Variables must be a JSON object');
                    return false;
                }
                const bad = Object.entries(parsed).find(([, v]) => typeof v !== 'string');
                if (bad) {
                    setJsonError(`Value for "${bad[0]}" must be a string`);
                    return false;
                }
                setJsonError('');
                return parsed as Record<string, string>;
            } catch {
                setJsonError('Invalid JSON');
                return false;
            }
        };
        const firePreview = useCallback(() => {
            const variables = parseVariablesJson();
            if (variables === false) return;
            preview.mutate({
                block_doc: blockDoc,
                subject: effectiveSubject || undefined,
                profile_id: !syntheticOnly && profileId ? Number(profileId) : undefined,
                variables
            });
        }, [blockDoc, effectiveSubject, profileId, variablesJson, preview, syntheticOnly]);
        useImperativeHandle(ref, () => ({ refresh: firePreview }), [firePreview]);
        if (channel === 'sms') {
            return (
                <div
                    className={layout.form}
                    style={{
                        padding: '1.2rem',
                        backgroundColor: 'var(--mark-warning-surface)',
                        borderRadius: '0.4rem',
                        margin: '1.2rem 0'
                    }}
                >
                    <P noDefaultMargin>
                        Server SMS preview is unavailable. The editor below shows the raw paragraph
                        content; final SMS validation runs at Drip Validate.
                    </P>
                </div>
            );
        }
        const previewData = preview.data?.data;
        const previewError = preview.error;
        const previewedDocHash = preview.variables
            ? JSON.stringify(preview.variables.block_doc)
            : null;
        const isOutOfDate =
            previewedDocHash !== null &&
            (previewedDocHash !== currentDocHash ||
                preview.variables?.subject !== (effectiveSubject || undefined));
        const refreshButton = (
            <MarkButton
                compact={compact}
                type="button"
                onClick={firePreview}
                disabled={preview.isPending || !!jsonError}
                variant="primary"
            >
                {preview.isPending ? 'Rendering…' : 'Refresh preview'}
            </MarkButton>
        );
        return (
            <div
                className={layout.form}
                style={{
                    margin: compact ? 0 : '1.6rem 0'
                }}
            >
                {compact && (
                    <div className={canvasStyles.deviceToolbar}>
                        <div
                            className={canvasStyles.actions}
                            role="group"
                            aria-label="Preview device"
                        >
                            <MarkButton
                                compact={compact}
                                size="regular"
                                aria-pressed={device === 'desktop'}
                                onClick={() => setDevice('desktop')}
                            >
                                Desktop
                            </MarkButton>
                            <MarkButton
                                compact={compact}
                                size="regular"
                                aria-pressed={device === 'mobile'}
                                onClick={() => setDevice('mobile')}
                            >
                                Mobile
                            </MarkButton>
                        </div>
                        {refreshButton}
                    </div>
                )}
                <details
                    className={compact ? canvasStyles.moreActions : undefined}
                    open={compact ? undefined : true}
                >
                    <summary
                        style={
                            compact
                                ? { cursor: 'pointer', color: 'var(--mark-muted)' }
                                : { display: 'none' }
                        }
                    >
                        Advanced preview settings
                    </summary>
                    <div
                        className={layout.form}
                        style={compact ? { marginTop: '1.6rem' } : undefined}
                    >
                        <div
                            style={{
                                flexWrap: 'wrap'
                            }}
                            className={layout.fieldGrid}
                        >
                            <div className={layout.field}>
                                <MarkLabel
                                    htmlFor={subjectId}
                                    style={{
                                        marginBottom: '0.4rem'
                                    }}
                                >
                                    Subject override
                                </MarkLabel>
                                <MarkInput
                                    compact={compact}
                                    id={subjectId}
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.currentTarget.value)}
                                    onFocus={clearInsertTarget}
                                    placeholder="Optional subject"
                                />
                            </div>
                            {!syntheticOnly && (
                                <div className={layout.field}>
                                    <MarkLabel
                                        htmlFor={profileIdFieldId}
                                        style={{
                                            marginBottom: '0.4rem'
                                        }}
                                    >
                                        Profile ID
                                    </MarkLabel>
                                    <MarkInput
                                        compact={compact}
                                        id={profileIdFieldId}
                                        type="number"
                                        value={profileId}
                                        onChange={(e) => setProfileId(e.currentTarget.value)}
                                        onFocus={clearInsertTarget}
                                        placeholder="Optional"
                                    />
                                </div>
                            )}
                            {!compact && refreshButton}
                        </div>
                        <div className={layout.field}>
                            <MarkLabel
                                htmlFor={variablesId}
                                style={{
                                    marginBottom: '0.4rem',
                                    display: 'block'
                                }}
                            >
                                Variables JSON override
                            </MarkLabel>
                            <MarkTextArea
                                id={variablesId}
                                aria-invalid={!!jsonError}
                                aria-describedby={jsonError ? `${variablesId}-error` : undefined}
                                value={variablesJson}
                                onFocus={clearInsertTarget}
                                onChange={(e) => {
                                    const val = e.currentTarget.value;
                                    setVariablesJson(val);
                                    if (!val.trim()) {
                                        setJsonError('');
                                    } else {
                                        try {
                                            const parsed = JSON.parse(val.trim());
                                            if (
                                                typeof parsed !== 'object' ||
                                                parsed === null ||
                                                Array.isArray(parsed)
                                            ) {
                                                setJsonError('Variables must be a JSON object');
                                            } else if (
                                                Object.values(parsed).some(
                                                    (v: unknown) => typeof v !== 'string'
                                                )
                                            ) {
                                                setJsonError('All variable values must be strings');
                                            } else {
                                                setJsonError('');
                                            }
                                        } catch {
                                            setJsonError('Invalid JSON');
                                        }
                                    }
                                }}
                                placeholder='{"FIRST_NAME": "Fictional reader"}'
                                rows={3}
                            />
                            {jsonError && (
                                <P
                                    id={`${variablesId}-error`}
                                    noDefaultMargin
                                    size="small"
                                    styles={{
                                        color: 'var(--mark-danger)',
                                        marginTop: '0.2rem'
                                    }}
                                >
                                    {jsonError}
                                </P>
                            )}
                        </div>
                    </div>
                </details>
                {previewError && (
                    <div
                        style={{
                            backgroundColor: 'var(--mark-danger-surface)',
                            marginBottom: '1.2rem',
                            color: 'var(--mark-danger)'
                        }}
                        className={layout.panel}
                    >
                        {extractErrorCodes(previewError).length > 0 ? (
                            <ul
                                style={{
                                    margin: 0,
                                    paddingLeft: '1.6rem'
                                }}
                            >
                                {extractErrorCodes(previewError).map((code, i) => (
                                    <li key={i}>{describeErrorCode(code)}</li>
                                ))}
                            </ul>
                        ) : (
                            <P noDefaultMargin size="small">
                                Preview error: {previewError.message}
                            </P>
                        )}
                    </div>
                )}
                {isOutOfDate && !preview.isPending && (
                    <div
                        role="status"
                        style={{
                            backgroundColor: 'var(--mark-warning-surface)',
                            marginBottom: '1.2rem',
                            color: 'var(--mark-warning)'
                        }}
                        className={layout.panel}
                    >
                        Preview out of date — the template changed since this render. Click
                        &quot;Refresh preview&quot; to update.
                    </div>
                )}
                {previewData?.rendered_subject !== undefined && (
                    <P
                        noDefaultMargin
                        size="small"
                        styles={{
                            marginBottom: '0.8rem',
                            color: 'var(--mark-muted)'
                        }}
                    >
                        {previewData.rendered_subject
                            ? `Subject: ${previewData.rendered_subject}`
                            : 'No subject preview (fill Subject override to test)'}
                    </P>
                )}
                {previewData?.html ? (
                    <div style={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                        <EmailPreviewFrame
                            html={previewData.html}
                            title="Template preview"
                            device={device}
                            height={compact ? 560 : 800}
                        />
                    </div>
                ) : (
                    !previewError && (
                        <div
                            style={{
                                height: 200,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px dashed var(--mark-border)',
                                borderRadius: '0.4rem',
                                color: 'var(--mark-muted)'
                            }}
                        >
                            <P noDefaultMargin>Click "Refresh preview" to render</P>
                        </div>
                    )
                )}
                {previewData?.variables && (
                    <details
                        style={{
                            marginTop: '0.8rem'
                        }}
                    >
                        <summary
                            style={{
                                cursor: 'pointer',
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Effective variables
                        </summary>
                        <pre
                            style={{
                                padding: '0.8rem',
                                backgroundColor: 'var(--mark-surface)',
                                borderRadius: '0.4rem',
                                overflow: 'auto',
                                maxHeight: 300
                            }}
                        >
                            {JSON.stringify(previewData.variables, null, 2)}
                        </pre>
                    </details>
                )}
            </div>
        );
    }
);
export default PreviewPanel;
